import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";

const API_BASE = "/api/ebay-intelligence";
const REVIEW_STATUSES = [
  { value: "open", label: "Open" },
  { value: "reviewed", label: "Reviewed" },
  { value: "paused_manually", label: "Paused manually" },
  { value: "budget_reduced", label: "Budget reduced" },
  { value: "campaign_ended", label: "Campaign ended" },
  { value: "deferred", label: "Deferred" },
  { value: "ignored", label: "Ignored" },
  { value: "needs_follow_up", label: "Needs follow-up" },
];
const FUNDING_FILTERS = [
  { value: "ALL", label: "All funding" },
  { value: "COST_PER_CLICK", label: "Priority CPC" },
  { value: "COST_PER_SALE", label: "Promoted CPS" },
  { value: "UNKNOWN", label: "Unknown" },
];

const numberText = (value) => Number(value || 0).toLocaleString();
const money = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const n = Number(value);
  return Number.isFinite(n) ? `$${n.toFixed(2)}` : String(value);
};
const ratioText = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const n = Number(value);
  return Number.isFinite(n) ? n.toFixed(2) : String(value);
};
const dateText = (value) => {
  if (!value) return "-";
  const date = new Date(String(value).replace(" ", "T"));
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
};
const statusLabel = (value) => REVIEW_STATUSES.find((item) => item.value === value)?.label || value || "Open";
const statusColor = (value) => {
  if (["paused_manually", "budget_reduced", "campaign_ended"].includes(value)) return "success";
  if (["ignored"].includes(value)) return "default";
  if (["deferred", "needs_follow_up"].includes(value)) return "warning";
  if (["reviewed"].includes(value)) return "info";
  return "default";
};
const severityColor = (value) => {
  if (value === "HIGH") return "error";
  if (value === "MEDIUM") return "warning";
  if (value === "LOW") return "info";
  return "default";
};

function SummaryCard({ label, value, helper }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, height: "100%" }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="h5" sx={{ fontWeight: 800, mt: 0.5 }}>{value ?? "--"}</Typography>
      {helper && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{helper}</Typography>}
    </Paper>
  );
}

export default function AdStopLossPanel() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({});
  const [rules, setRules] = useState([]);
  const [schemaReady, setSchemaReady] = useState(true);
  const [reviewSchemaReady, setReviewSchemaReady] = useState(true);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [fundingModel, setFundingModel] = useState("ALL");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [includeResolved, setIncludeResolved] = useState(false);
  const [days, setDays] = useState(30);
  const [minSpend, setMinSpend] = useState(0);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [draft, setDraft] = useState(null);

  const loadStopLoss = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        limit: "500",
        days: String(days || 30),
        min_spend: String(minSpend || 0),
      });
      if (fundingModel !== "ALL") params.set("funding_model", fundingModel);
      if (includeResolved) params.set("include_resolved", "true");
      const [candidateRes, rulesRes] = await Promise.all([
        fetch(`${API_BASE}/ad-stop-loss?${params.toString()}`),
        fetch(`${API_BASE}/ad-stop-loss/rules`),
      ]);
      const candidateJson = await candidateRes.json();
      const rulesJson = await rulesRes.json();
      if (!candidateRes.ok && candidateJson.schema_ready !== false) throw new Error(candidateJson.error || candidateJson.message || "Failed to load ad stop-loss candidates");
      if (!rulesRes.ok || rulesJson.ok === false) throw new Error(rulesJson.error || "Failed to load ad stop-loss rules");
      setSchemaReady(candidateJson.schema_ready !== false);
      setReviewSchemaReady(candidateJson.review_schema_ready !== false);
      setRows(Array.isArray(candidateJson.rows) ? candidateJson.rows : []);
      setSummary(candidateJson.summary || {});
      setRules(Array.isArray(rulesJson.rules) ? rulesJson.rules : []);
      if (candidateJson.schema_ready === false) setMessage(candidateJson.message || "Manual SQL install is required before stop-loss candidates are available.");
    } catch (e) {
      setError(e.message || String(e));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [days, fundingModel, includeResolved, minSpend]);

  useEffect(() => {
    loadStopLoss();
  }, [loadStopLoss]);

  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return rows.filter((row) => {
      if (statusFilter !== "ALL" && (row.review_status || "open") !== statusFilter) return false;
      if (!term) return true;
      return [row.sku, row.card_name, row.title, row.rule_key, row.rule_label, row.reason, row.review?.operator_note].some((value) => String(value || "").toLowerCase().includes(term));
    });
  }, [rows, search, statusFilter]);

  const openReview = (row) => {
    setDraft({
      recommendation_key: row.recommendation_key,
      listing_id: row.listing_id || "",
      sku: row.sku || "",
      ebay_listing_id: row.ebay_listing_id || "",
      ebay_offer_id: row.ebay_offer_id || "",
      funding_model: row.funding_model || "",
      rule_key: row.rule_key,
      review_status: row.review_status || "open",
      review_reason: row.reason || "",
      operator_note: row.review?.operator_note || "",
      operator_name: row.review?.operator_name || "",
      follow_up_at: row.review?.follow_up_at || "",
      row,
    });
    setDialogOpen(true);
  };

  const saveReview = async () => {
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      const res = await fetch(`${API_BASE}/ad-stop-loss/reviews`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(draft),
      });
      const json = await res.json();
      if (!res.ok || !json.ok) throw new Error(json.error || json.message || "Failed to save stop-loss review");
      setDialogOpen(false);
      setMessage(json.message || "Stop-loss review saved.");
      await loadStopLoss();
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setSaving(false);
    }
  };

  const columns = useMemo(() => [
    { field: "rule_key", headerName: "Rule", flex: 0.9, minWidth: 150, renderCell: (params) => <Chip size="small" color={severityColor(params.row.severity)} label={params.row.rule_label || params.value} /> },
    { field: "title", headerName: "Listing", flex: 1.3, minWidth: 240, renderCell: (params) => (
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>{params.row.title || params.row.card_name || "Untitled"}</Typography>
        <Typography variant="caption" color="text.secondary" noWrap>{params.row.sku || "No SKU"}</Typography>
      </Box>
    ) },
    { field: "funding_model", headerName: "Funding", flex: 0.7, minWidth: 130, renderCell: (params) => <Chip size="small" label={params.value === "COST_PER_CLICK" ? "Priority CPC" : params.value === "COST_PER_SALE" ? "CPS" : (params.value || "Unknown")} /> },
    { field: "total_ad_cost", headerName: "Ad cost", flex: 0.6, minWidth: 105, valueGetter: (value, row) => row?.metrics?.total_ad_cost, valueFormatter: (value) => money(value) },
    { field: "cpc_spend", headerName: "CPC", flex: 0.6, minWidth: 95, valueGetter: (value, row) => row?.metrics?.cpc_spend, valueFormatter: (value) => money(value) },
    { field: "ad_sales", headerName: "Ad sales", flex: 0.6, minWidth: 105, valueGetter: (value, row) => row?.metrics?.ad_sales, valueFormatter: (value) => money(value) },
    { field: "roas", headerName: "ROAS", flex: 0.5, minWidth: 90, valueGetter: (value, row) => row?.metrics?.roas, valueFormatter: (value) => ratioText(value) },
    { field: "profit_after_ad", headerName: "Profit after ad", flex: 0.7, minWidth: 125, valueGetter: (value, row) => row?.metrics?.profit_after_ad, valueFormatter: (value) => money(value) },
    { field: "review_status", headerName: "Review", flex: 0.7, minWidth: 145, renderCell: (params) => <Chip size="small" color={statusColor(params.value)} label={statusLabel(params.value)} /> },
    { field: "actions", headerName: "", sortable: false, filterable: false, width: 110, renderCell: (params) => <Button size="small" onClick={() => openReview(params.row)}>Review</Button> },
  ], []);

  return (
    <Stack spacing={2}>
      {(loading || saving) && <LinearProgress />}
      {!schemaReady && <Alert severity="warning">Manual telemetry SQL is required before ad stop-loss candidates can be calculated.</Alert>}
      {!reviewSchemaReady && <Alert severity="warning">Install the ad stop-loss review SQL before operator review status can be saved.</Alert>}
      {error && <Alert severity="error">{error}</Alert>}
      {message && <Alert severity="success">{message}</Alert>}
      <Alert severity="info">
        Stop-loss candidates are advisory and local-only. Review saves do not update eBay campaigns, bids, budgets, listings, prices, or quantities.
      </Alert>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}><SummaryCard label="Candidates" value={numberText(summary.total_candidates)} helper={`${numberText(summary.high_priority_candidates)} high priority`} /></Grid>
        <Grid item xs={12} sm={6} md={3}><SummaryCard label="Priority CPC" value={numberText(summary.cpc_stop_loss_candidates)} helper="CPC-specific stop-loss rows" /></Grid>
        <Grid item xs={12} sm={6} md={3}><SummaryCard label="At-risk spend" value={money(summary.total_at_risk_spend)} helper="Current review window" /></Grid>
        <Grid item xs={12} sm={6} md={3}><SummaryCard label="Open reviews" value={numberText(summary.review_status_counts?.open)} helper={`${numberText(summary.review_status_counts?.reviewed)} reviewed`} /></Grid>
      </Grid>

      <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ xs: "stretch", md: "center" }} sx={{ mb: 2, flexWrap: "wrap" }}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="ad-stop-loss-funding-label">Funding</InputLabel>
            <Select labelId="ad-stop-loss-funding-label" value={fundingModel} label="Funding" onChange={(event) => setFundingModel(event.target.value)}>
              {FUNDING_FILTERS.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="ad-stop-loss-status-label">Review</InputLabel>
            <Select labelId="ad-stop-loss-status-label" value={statusFilter} label="Review" onChange={(event) => setStatusFilter(event.target.value)}>
              <MenuItem value="ALL">All open</MenuItem>
              {REVIEW_STATUSES.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField size="small" type="number" label="Days" value={days} onChange={(event) => setDays(event.target.value)} sx={{ width: 100 }} />
          <TextField size="small" type="number" label="Min spend" value={minSpend} onChange={(event) => setMinSpend(event.target.value)} sx={{ width: 130 }} />
          <TextField size="small" label="Search" value={search} onChange={(event) => setSearch(event.target.value)} sx={{ minWidth: 220 }} />
          <FormControlLabel control={<Switch checked={includeResolved} onChange={(event) => setIncludeResolved(event.target.checked)} />} label="Include resolved" />
          <Button variant="outlined" onClick={loadStopLoss} disabled={loading || saving}>Refresh</Button>
        </Stack>
        <Box sx={{ height: 560 }}>
          <DataGrid
            rows={filteredRows}
            columns={columns}
            getRowId={(row) => row.recommendation_key}
            disableRowSelectionOnClick
            density="compact"
            pageSizeOptions={[25, 50, 100]}
            initialState={{ pagination: { paginationModel: { pageSize: 50 } } }}
            localeText={{ noRowsLabel: schemaReady ? "No ad stop-loss candidates match this filter." : "Install the required SQL before candidates can be calculated." }}
          />
        </Box>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>Active rules</Typography>
        <Grid container spacing={1.5}>
          {rules.map((rule) => (
            <Grid item xs={12} md={6} key={rule.rule_key}>
              <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, p: 1.5, height: "100%" }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5, flexWrap: "wrap" }}>
                  <Chip size="small" color={severityColor(rule.severity)} label={rule.severity} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{rule.label}</Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary">{rule.description}</Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Paper>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Ad stop-loss review</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="info">This saves local review tracking only. Campaign changes remain manual in eBay.</Alert>
            <Typography variant="body2">
              {draft?.row?.title || draft?.sku || "Selected candidate"} - {draft?.rule_key}
            </Typography>
            <FormControl fullWidth size="small">
              <InputLabel id="ad-stop-loss-review-status-label">Review status</InputLabel>
              <Select labelId="ad-stop-loss-review-status-label" value={draft?.review_status || "open"} label="Review status" onChange={(event) => setDraft((current) => ({ ...current, review_status: event.target.value }))}>
                {REVIEW_STATUSES.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField label="Reason" value={draft?.review_reason || ""} onChange={(event) => setDraft((current) => ({ ...current, review_reason: event.target.value }))} fullWidth size="small" />
            <TextField label="Operator note" value={draft?.operator_note || ""} onChange={(event) => setDraft((current) => ({ ...current, operator_note: event.target.value }))} multiline minRows={4} fullWidth />
            <TextField label="Operator" value={draft?.operator_name || ""} onChange={(event) => setDraft((current) => ({ ...current, operator_name: event.target.value }))} fullWidth size="small" />
            <TextField label="Follow-up" type="datetime-local" InputLabelProps={{ shrink: true }} value={draft?.follow_up_at || ""} onChange={(event) => setDraft((current) => ({ ...current, follow_up_at: event.target.value }))} fullWidth size="small" />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={saveReview} disabled={saving || !draft || !reviewSchemaReady}>Save Review</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
